import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '', inquiryType: '', message: '', agreeToContact: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const inquiryTypes = [
    { label: 'Sales Inquiry', value: 'sales' },
    { label: 'Technical Support', value: 'support' },
    { label: 'Partnership Opportunity', value: 'partnership' },
    { label: 'General Question', value: 'general' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => { setIsSubmitting(false); setSubmitted(true); }, 1500);
  };

  const handleInputChange = (field: string, value: any) => setFormData(prev => ({ ...prev, [field]: value }));

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  if (submitted) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', padding: '2rem' }}>
        <div style={{ ...cardStyle, textAlign: 'center', maxWidth: '500px' }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
            <i className="pi pi-check" style={{ color: 'white', fontSize: '2rem' }}></i>
          </div>
          <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Message Sent Successfully!</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Thank you for contacting us. We'll get back to you within 24 hours.</p>
          <Button label="Return to Homepage" icon="pi pi-home" onClick={() => window.location.href = '/'} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#1f2937' }}>Get in Touch</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto' }}>
          Ready to transform your research workflow? We're here to help you get started.
        </p>
      </div>

      {/* Contact Form and Info */}
      <div style={{ padding: '3rem 2rem', backgroundColor: 'white' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          {/* Form */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: '1.5rem', color: '#1f2937', margin: '0 0 1.5rem 0' }}>Send us a message</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>First Name *</label>
                  <InputText value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} className="w-full" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Last Name *</label>
                  <InputText value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} className="w-full" required />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Email Address *</label>
                <InputText type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full" required />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Company/Organization</label>
                <InputText value={formData.company} onChange={(e) => handleInputChange('company', e.target.value)} className="w-full" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Inquiry Type *</label>
                <Dropdown value={formData.inquiryType} onChange={(e) => handleInputChange('inquiryType', e.value)} options={inquiryTypes} placeholder="Select inquiry type" className="w-full" required />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Message *</label>
                <InputTextarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} rows={4} className="w-full" placeholder="Tell us about your research needs..." required />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <Checkbox checked={formData.agreeToContact} onChange={(e) => handleInputChange('agreeToContact', e.checked)} />
                <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>I agree to be contacted regarding this inquiry. *</label>
              </div>
              <Button type="submit" label={isSubmitting ? 'Sending...' : 'Send Message'} icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'} className="w-full p-button-lg" disabled={isSubmitting || !formData.agreeToContact} />
            </form>
          </div>

          {/* Contact Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={cardStyle}>
              <h3 style={{ fontSize: '1.125rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Quick Actions</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Button label="Explore Platform" icon="pi pi-arrow-right" className="p-button-lg" onClick={() => window.location.href = '/platform'} />
                <Button label="View Solutions" icon="pi pi-lightbulb" className="p-button-outlined p-button-lg" onClick={() => window.location.href = '/solutions'} />
              </div>
            </div>

            <div style={cardStyle}>
              <h3 style={{ fontSize: '1.125rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Other Ways to Reach Us</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { icon: 'pi pi-envelope', label: 'Email', value: 'info@insights.ps' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '8px', backgroundColor: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={item.icon} style={{ color: 'white' }}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, color: '#1f2937', fontSize: '0.875rem' }}>{item.label}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={cardStyle}>
              <h3 style={{ fontSize: '1.125rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Enterprise Support</h3>
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                  <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                  Enterprise customers have 24/7 priority support
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Office Info */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Our Office</h2>
          <p style={{ color: '#6b7280' }}>Visit us at our headquarters or connect virtually</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '1.125rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Headquarters</h3>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
              <i className="pi pi-map-marker" style={{ color: '#3B82F6', marginTop: '0.25rem' }}></i>
              <div>
                <div style={{ fontWeight: 500, color: '#1f2937', marginBottom: '0.25rem' }}>Address</div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Ramallah, Palestine</div>
              </div>
            </div>
          </div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '1.125rem', color: '#1f2937', margin: '0 0 1rem 0' }}>Global Presence</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              While our headquarters are in Ramallah, we serve clients worldwide with team members across multiple time zones.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button icon="pi pi-linkedin" className="p-button-text p-button-sm" />
              <Button icon="pi pi-twitter" className="p-button-text p-button-sm" />
              <Button icon="pi pi-github" className="p-button-text p-button-sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
